'use client'

import { Cell, Divider, Text } from '@vinted/web-ui'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { SortByOption, Filter } from '@marketplace-web/catalog/catalog-data'
import { useCatalogContext, clickFilterEvent } from '@marketplace-web/catalog/catalog-page-data'

import SelectableItemList, { SelectableListItem } from '../../common/SelectableItemList'
import useFetchDynamicFilters from '../../../../hooks/useFetchDynamicFilters'
import FilterDropdown from '../../common/FilterDropdown'

type Props = {
  learnMoreUrl: string | null | undefined
  useDropdownBottomEndPlacement?: boolean
}

type SortItem = SelectableListItem<SortByOption, SortByOption>

const SortFilter = ({ useDropdownBottomEndPlacement = false, learnMoreUrl }: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('catalog.filters.sort')
  const fetchDynamicFilters = useFetchDynamicFilters()
  const {
    state: {
      filters,
      dynamicFilters,
      items: { searchTrackingParams },
    },
  } = useCatalogContext()
  const { globalSearchSessionId, globalCatalogBrowseSessionId } = searchTrackingParams
  const searchSessionId = searchTrackingParams.sessionId
  const { selectedDynamicFilters } = dynamicFilters

  const selected = filters.sortBy || SortByOption.Relevance

  const handleOnFilterClick = () => {
    track(
      clickFilterEvent({
        filter: Filter.Sort,
        screen: 'search_filter',
        searchSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
      }),
    )
  }

  const handleItemClick = (item: SortItem) => {
    fetchDynamicFilters({
      selectedDynamicFilters,
      selectedFilters: { sortBy: item.value },
    })
  }

  const renderLearnMoreMessage = () => {
    if (!learnMoreUrl) return null

    const action = (
      <a key="action" href={learnMoreUrl}>
        {translate('learn_more.action')}
      </a>
    )

    return (
      <>
        <Divider />
        <Cell>
          <Text
            type={Text.Type.Caption}
            text={translate('learn_more.message', { action })}
            as="span"
          />
        </Cell>
      </>
    )
  }

  const renderDropdown = () => {
    const items = Object.values(SortByOption).map(option => ({
      id: option,
      title: translate(`options.${option}`),
      value: option,
    }))

    return (
      <div className="u-ui-padding-vertical-x-small" data-testid="sort-by-dropdown">
        <SelectableItemList<SortByOption, SortByOption>
          name="sort_by"
          isMultiSelect={false}
          items={items}
          selected={[selected || SortByOption.Relevance]}
          onItemClick={handleItemClick}
          itemButtonTestId="sort-by-list"
          disableButtonClicks
        />
        {renderLearnMoreMessage()}
      </div>
    )
  }

  return (
    <div className="u-ui-margin-right-regular u-ui-margin-bottom-regular">
      <FilterDropdown
        title={translate('name')}
        placement={useDropdownBottomEndPlacement ? 'bottom-end' : undefined}
        areAnyOfItemsSelected={!!selected && selected !== SortByOption.Relevance}
        testId="catalog--sort-filter"
        onFilterClick={handleOnFilterClick}
        closeOnClick
        useChipAsTrigger
      >
        {renderDropdown()}
      </FilterDropdown>
    </div>
  )
}

export default SortFilter
